import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Calendar, DollarSign, Store, Users, Clock, Building2, Star, Crown, ShieldCheck, ArrowLeft, ArrowUp, Play, MapPin, Tag, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
// AI Chat disabled - will reconnect when more shows/exhibitors available
// import AIChat from '../components/AIChat';
import PayPalPayment from '../components/PayPalPayment';
import { useCurrency } from '../contexts/CurrencyContext';
import AutoTranslatedText from '../components/AutoTranslatedText';
import OptimizedImage from '../components/OptimizedImage';
import VideoPlayer from '../components/VideoPlayer';
import SEOHead from '../components/SEOHead';
import { generateEventWithVideoSchema, generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';
import { parseLocalDate } from '../utils/dateUtils';

// Region data with flags and names
const REGIONS = {
  us: { flag: '🇺🇸', name: 'United States' },
  ca: { flag: '🇨🇦', name: 'Canada' },
  fr: { flag: '🇫🇷', name: 'France' },
  de: { flag: '🇩🇪', name: 'Germany' },
  es: { flag: '🇪🇸', name: 'Spain' },
  gb: { flag: '🇬🇧', name: 'United Kingdom' },
  eu: { flag: '🇪🇺', name: 'Europe' },
  global: { flag: '🌍', name: 'Global' }
};

const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const { formatPrice, getCurrencyCode } = useCurrency();
  const [event, setEvent] = useState(null);
  const [booths, setBooths] = useState([]);
  const [registered, setRegistered] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showBuyerPayment, setShowBuyerPayment] = useState(false);
  const [showVendorPayment, setShowVendorPayment] = useState(false);
  const [selectedTier, setSelectedTier] = useState('premium');
  const [registrationStats, setRegistrationStats] = useState(null);
  
  // Coupon state
  const [buyerCouponCode, setBuyerCouponCode] = useState('');
  const [buyerCouponApplied, setBuyerCouponApplied] = useState(null);
  const [buyerCouponLoading, setBuyerCouponLoading] = useState(false);
  const [vendorCouponCode, setVendorCouponCode] = useState('');
  const [vendorCouponApplied, setVendorCouponApplied] = useState(null);
  const [vendorCouponLoading, setVendorCouponLoading] = useState(false);

  useEffect(() => {
    fetchEventData();
  }, [eventId]);
  
  // Validate coupon code
  const validateCoupon = async (code, userType, setCouponApplied, setLoading) => {
    if (!code.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/events/${eventId}/validate-coupon`, {
        code: code.trim(),
        user_type: userType
      });
      
      if (response.data.valid) {
        setCouponApplied({
          code: code.trim().toUpperCase(),
          discount_percent: response.data.discount_percent,
          uses_remaining: response.data.uses_remaining
        });
        toast.success(`Coupon applied! ${response.data.discount_percent}% discount`);
      }
    } catch (error) {
      setCouponApplied(null);
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate discounted price
  const getDiscountedPrice = (originalPrice, couponApplied) => {
    if (!couponApplied) return originalPrice;
    const discount = (originalPrice * couponApplied.discount_percent) / 100;
    return Math.max(0, originalPrice - discount);
  };
  
  // Record coupon usage after successful payment
  const recordCouponUsage = async (code, userType) => {
    if (!code) return;
    try {
      await axios.post(`${API}/events/${eventId}/use-coupon`, {
        code: code,
        user_type: userType
      });
    } catch (error) {
      console.error('Failed to record coupon usage', error);
    }
  };
  
  // Reset coupon when modal closes
  const handleBuyerModalClose = (open) => {
    if (!open) {
      setBuyerCouponCode('');
      setBuyerCouponApplied(null);
    }
    setShowBuyerPayment(open);
  };
  
  const handleVendorModalClose = (open) => {
    if (!open) {
      setVendorCouponCode('');
      setVendorCouponApplied(null);
    }
    setShowVendorPayment(open);
  };

  const fetchEventData = async () => {
    try {
      const [eventRes, boothsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}`),
        axios.get(`${API}/booths?event_id=${eventId}`),
      ]);
      setEvent(eventRes.data);
      
      // Fetch registration stats if enabled
      if (eventRes.data.show_registration_stats) {
        try {
          const statsRes = await axios.get(`${API}/events/${eventId}/registration-stats`);
          setRegistrationStats(statsRes.data);
        } catch (e) {
          console.error('Failed to fetch registration stats', e);
        }
      }
      
      // Sort booths: Elite > Premium > Basic, then alphabetically
      const sortedBooths = boothsRes.data.sort((a, b) => {
        const tierOrder = { elite: 0, premium: 1, basic: 2 };
        const tierA = tierOrder[a.tier] ?? 3;
        const tierB = tierOrder[b.tier] ?? 3;
        if (tierA !== tierB) return tierA - tierB;
        return a.company_name.localeCompare(b.company_name);
      });
      setBooths(sortedBooths);

      if (user) {
        const regsRes = await axios.get(`${API}/registrations`);
        const isReg = regsRes.data.some((r) => r.event_id === eventId);
        setRegistered(isReg);
      }
    } catch (error) {
      console.error('Failed to fetch event', error);
      toast.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinQueue = async () => {
    if (!user) {
      toast.error('Please sign in to join queue');
      return;
    }

    try {
      await axios.post(`${API}/queue?event_id=${eventId}`);
      toast.success('Joined queue successfully!');
    } catch (error) {
      console.error('Queue join failed', error);
      toast.error('Failed to join queue');
    }
  };

  const getTierBadge = (tier) => {
    if (tier === 'elite') {
      return (
        <Badge className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white border-0 shadow-md px-3 py-1">
          <Crown className="h-3 w-3 mr-1 fill-yellow-300 text-yellow-300" />
          {t('event_detail.tier_elite')}
        </Badge>
      );
    }
    if (tier === 'premium') {
      return (
        <Badge className="bg-orange-100 text-orange-700 border border-orange-200">
          <Star className="h-3 w-3 mr-1 fill-orange-500" />
          {t('event_detail.tier_premium')}
        </Badge>
      );
    }
    return (
      <Badge className="bg-slate-100 text-slate-700 border border-slate-200">
        {t('event_detail.tier_basic')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('event_detail.loading')}</p>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>{t('event_detail.not_found')}</p>
      </div>
    );
  }

  // Generate structured data for this event (includes VideoObject if video present)
  const eventStructuredData = generateEventWithVideoSchema(event);
  
  // Generate breadcrumb schema for better search results navigation
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Events', url: '/events' },
    { name: event.name }
  ]);
  
  // Combine all schemas
  const allStructuredData = Array.isArray(eventStructuredData) 
    ? [...eventStructuredData, breadcrumbSchema]
    : [eventStructuredData, breadcrumbSchema];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="event-detail-page">
      {/* SEO with Event, Video, and Breadcrumb Schema */}
      <SEOHead 
        title={`${event.name} | TradeHub24 Virtual Tradeshow`}
        description={event.description?.substring(0, 160) || `Join ${event.name} virtual tradeshow on TradeHub24. Connect with exhibitors and discover products.`}
        canonicalUrl={`${SEO_CONFIG.siteUrl}/events/${eventId}`}
        ogImage={event.banner_url || SEO_CONFIG.ogImage}
        ogType="website"
        structuredData={allStructuredData}
      />
      
      {/* Compact Hero Section - Banner + Info Side by Side */}
      <div className="bg-gradient-to-br from-primary to-primary/80 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Back to Events Button */}
          <Link to="/events" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('event_detail.back_to_events')}
          </Link>
          
          {/* Two Column Layout: Banner + Info */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
            {/* Event Banner */}
            <div className="lg:col-span-1">
              <div className="bg-white/10 rounded-lg overflow-hidden aspect-video flex items-center justify-center">
                {event.banner_url ? (
                  <OptimizedImage 
                    src={event.banner_url}
                    alt={event.name}
                    className="w-full h-full"
                    objectFit="cover"
                    fallbackClassName="bg-white/10"
                  />
                ) : (
                  <Calendar className="h-16 w-16 text-white/50" />
                )}
              </div>
            </div>
            
            {/* Event Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  event.status === 'live' ? 'bg-accent' : 'bg-white/20'
                }`}>
                  {event.status === 'live' ? t('event_detail.live_now') : event.status.toUpperCase()}
                </span>
                {/* Region Badge */}
                {event.region && REGIONS[event.region] && (
                  <span className="px-2 py-1 rounded text-xs font-medium bg-white/20 flex items-center gap-1">
                    <span className="text-base">{REGIONS[event.region].flag}</span>
                    <span>{REGIONS[event.region].name}</span>
                  </span>
                )}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="event-title">
                <AutoTranslatedText text={event.name} translations={event.name_translations} showLoading={false} />
              </h1>
              <p className="text-sm opacity-90 mb-4 line-clamp-2">
                <AutoTranslatedText text={event.description} translations={event.description_translations} showLoading={false} />
              </p>
              <div className="flex flex-wrap gap-4 text-xs">
                <div className="flex items-center space-x-1 bg-white/10 rounded px-2 py-1">
                  <Calendar className="h-4 w-4" />
                  <span className="font-mono">{parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center space-x-1 bg-white/10 rounded px-2 py-1">
                  <Store className="h-4 w-4" />
                  <span>{booths.length} {t('event_detail.exhibitors')}</span>
                </div>
                <div className="flex items-center space-x-1 bg-white/10 rounded px-2 py-1">
                  <DollarSign className="h-4 w-4" />
                  <span className="font-mono">{formatPrice(event.buyer_fee, true)} {t('event_detail.entry')}</span>
                </div>
                {/* Registration Stats */}
                {registrationStats && registrationStats.show_attendee_count && (
                  <div className="flex items-center space-x-1 bg-white/10 rounded px-2 py-1">
                    <Users className="h-4 w-4" />
                    <span>{registrationStats.attendee_count} {t('events.attendees')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Registration Stats Banner */}
        {registrationStats && (registrationStats.show_attendee_count || registrationStats.show_exhibitor_count) && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-4 mb-6">
            <div className="flex flex-wrap items-center justify-center gap-6 text-center">
              {registrationStats.show_attendee_count && (
                <div className="flex items-center gap-2">
                  <div className="bg-blue-100 rounded-full p-2">
                    <Users className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{registrationStats.attendee_count}</p>
                    <p className="text-xs text-blue-600">{t('events.registered_attendees') || 'Registered Attendees'}</p>
                  </div>
                </div>
              )}
              {registrationStats.show_exhibitor_count && (
                <div className="flex items-center gap-2">
                  <div className="bg-indigo-100 rounded-full p-2">
                    <Building2 className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-indigo-700">{registrationStats.exhibitor_count}</p>
                    <p className="text-xs text-indigo-600">{t('events.exhibitors') || 'Exhibitors'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Event Introduction Video - Language-aware */}
        {(() => {
          // Get video URL based on current language (fallback to English/default)
          const getVideoForLanguage = () => {
            const lang = i18n.language;
            if (lang === 'fr' && event.intro_video_url_fr) return event.intro_video_url_fr;
            if (lang === 'es' && event.intro_video_url_es) return event.intro_video_url_es;
            if (lang === 'de' && event.intro_video_url_de) return event.intro_video_url_de;
            return event.intro_video_url; // Default (English)
          };
          
          const videoUrl = getVideoForLanguage();
          
          if (!videoUrl) return null;
          
          // Show which language video is being displayed
          const videoLang = i18n.language;
          const hasLocalizedVideo = 
            (videoLang === 'fr' && event.intro_video_url_fr) ||
            (videoLang === 'es' && event.intro_video_url_es) ||
            (videoLang === 'de' && event.intro_video_url_de) ||
            videoLang === 'en';
          
          return (
            <div className="bg-white border border-slate-200 rounded-md p-4 mb-6">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Play className="h-5 w-5 text-primary" />
                  <h3 className="text-base font-semibold">{t('event_detail.watch_intro') || 'Watch Event Introduction'}</h3>
                </div>
                {!hasLocalizedVideo && i18n.language !== 'en' && (
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                    Video in English
                  </span>
                )}
              </div>
              <div className="w-full">
                <VideoPlayer 
                  src={videoUrl}
                  poster={event.banner_url}
                  title={event.name}
                  className="shadow-md rounded-lg"
                />
              </div>
            </div>
          );
        })()}

        {/* Registration Actions */}
        <div className="bg-white border border-slate-200 rounded-md p-6 mb-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold mb-1">{t('event_detail.join_exhibit')}</h3>
              <p className="text-sm text-slate-600">
                {user?.role === 'buyer' && registered 
                  ? t('event_detail.registered_msg')
                  : user?.role === 'seller'
                  ? t('event_detail.vendor_msg')
                  : t('event_detail.guest_msg')}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {/* Buyer Registration */}
              {(!user || user.role === 'buyer') && (
                <>
                  {user && registered ? (
                    event.status === 'live' ? (
                      <Button onClick={handleJoinQueue} variant="outline" data-testid="join-queue-btn">
                        <Clock className="mr-2 h-4 w-4" /> {t('dashboard.queue')}
                      </Button>
                    ) : (
                      <Button variant="outline" className="text-amber-600 border-amber-300" disabled data-testid="join-queue-locked-btn">
                        <Lock className="mr-2 h-4 w-4" /> {t('event_detail.queue_opens_live')}
                      </Button>
                    )
                  ) : (
                    <Button 
                      onClick={() => user ? setShowBuyerPayment(true) : navigate('/auth')} 
                      data-testid="register-buyer-btn"
                    >
                      <Users className="mr-2 h-4 w-4" />
                      {t('event_detail.register_attendee')} ({formatPrice(event.buyer_fee, true)})
                    </Button>
                  )}
                </>
              )}
              
              {/* Vendor Registration */}
              {(!user || user.role === 'seller') && (
                event.status === 'registration_open' || 
                event.status === 'upcoming' || 
                (event.status === 'live' && event.allow_exhibitor_registration_during_live)
              ) && (() => {
                const userBooth = user && booths.find(b => b.seller_id === user.id);
                if (userBooth) {
                  // Vendor already has a booth - show upgrade option
                  return (
                    <Button 
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      data-testid="upgrade-tier-btn"
                    >
                      <ArrowUp className="mr-2 h-4 w-4" />
                      {t('event_detail.upgrade_next_tier')}
                    </Button>
                  );
                }
                return (
                  <Button 
                    onClick={() => user ? setShowVendorPayment(true) : navigate('/auth')}
                    variant={user?.role === 'seller' ? 'default' : 'outline'}
                    data-testid="register-vendor-btn"
                  >
                    <Store className="mr-2 h-4 w-4" />
                    {t('event_detail.register_vendor')}
                  </Button>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Exhibitors */}
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('event_detail.exhibitors')} ({booths.length})</h2>
          
          {/* Non-live event: info banner */}
          {event.status !== 'live' && booths.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3" data-testid="exhibitors-locked-banner">
              <Lock className="h-5 w-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">{t('event_detail.exhibitors_locked')}</p>
                <p className="text-xs text-amber-600">{t('event_detail.exhibitors_locked_desc')}</p>
              </div>
            </div>
          )}
          
          {booths.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-md p-12 text-center">
              <Store className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">{t('event_detail.no_exhibitors')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {booths.map((booth) => {
                const isLive = event.status === 'live';
                const CardWrapper = isLive ? Link : 'div';
                const wrapperProps = isLive 
                  ? { to: `/booth/${booth.id}`, className: 'group', 'data-testid': `booth-card-${booth.id}` }
                  : { 
                      className: 'group cursor-not-allowed', 
                      'data-testid': `booth-card-${booth.id}`,
                      onClick: () => toast.info(t('event_detail.exhibitor_details_live'), { duration: 3000 })
                    };
                return (
                <CardWrapper key={booth.id} {...wrapperProps}>
                  <div className={`bg-white rounded-lg overflow-hidden transition-all relative ${
                    !isLive ? 'opacity-75' : 'hover:shadow-lg'
                  } ${
                    booth.tier === 'elite' 
                      ? 'border-2 border-purple-500 ring-2 ring-purple-200 shadow-lg shadow-purple-100' 
                      : booth.tier === 'premium'
                      ? 'border border-orange-300 hover:border-orange-400'
                      : 'border border-slate-200 group-hover:border-primary'
                  }`}>
                    {/* Locked overlay for non-live events */}
                    {!isLive && (
                      <div className="absolute top-3 right-3 z-10 bg-amber-100 text-amber-700 rounded-full px-2.5 py-1 flex items-center gap-1 text-xs font-medium" data-testid="booth-locked-badge">
                        <Lock className="h-3 w-3" /> {t('event_detail.locked')}
                      </div>
                    )}
                    
                    {/* Elite Special Header */}
                    {booth.tier === 'elite' && (
                      <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 text-white px-4 py-2 flex items-center justify-center">
                        <Crown className="h-4 w-4 mr-2 fill-yellow-300 text-yellow-300" />
                        <span className="text-sm font-bold tracking-wide">ELITE EXHIBITOR</span>
                        <Crown className="h-4 w-4 ml-2 fill-yellow-300 text-yellow-300" />
                      </div>
                    )}
                    
                    {/* Elite Banner - only for elite tier */}
                    {booth.tier === 'elite' && booth.banner_url && (
                      <div className={`aspect-[3/1] bg-gradient-to-br from-purple-100 to-purple-50 overflow-hidden ${!isLive ? 'blur-sm' : ''}`}>
                        <OptimizedImage 
                          src={booth.banner_url}
                          alt={booth.company_name}
                          className="w-full h-full"
                        />
                      </div>
                    )}
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        {/* Logo */}
                        <div className={`w-16 h-16 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0 ${
                          booth.tier === 'elite' 
                            ? 'bg-gradient-to-br from-purple-100 to-indigo-100 ring-2 ring-purple-300' 
                            : 'bg-slate-100'
                        }`}>
                          {booth.logo_url ? (
                            <OptimizedImage 
                              src={booth.logo_url}
                              alt={booth.company_name}
                              className="max-w-full max-h-full"
                            />
                          ) : (
                            <Building2 className={`h-8 w-8 ${booth.tier === 'elite' ? 'text-purple-400' : 'text-slate-400'}`} />
                          )}
                        </div>
                        {/* Tier Badge */}
                        {getTierBadge(booth.tier)}
                      </div>
                      <h3 className={`text-lg font-semibold mb-2 ${booth.tier === 'elite' ? 'text-purple-900' : 'text-slate-900'}`}>{booth.company_name}</h3>
                      {isLive ? (
                        <p className="text-sm text-slate-600 line-clamp-2">
                          <AutoTranslatedText text={booth.description} translations={booth.description_translations} showLoading={false} />
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">{t('event_detail.details_when_live')}</p>
                      )}
                    </div>
                  </div>
                </CardWrapper>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Buyer Registration Payment Modal */}
      <Dialog open={showBuyerPayment} onOpenChange={handleBuyerModalClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('event_detail.register_attendee') || 'Register as Attendee'}</DialogTitle>
            <DialogDescription>
              {t('event_detail.pay_entry_fee') || 'Pay the entry fee to access all exhibitor booths at'} {event?.name}
            </DialogDescription>
          </DialogHeader>
          
          {/* Coupon Code Section - Show for attendees if coupon enabled and applies to attendees or both */}
          {event?.coupon_enabled && (event?.coupon_applies_to === 'attendees' || event?.coupon_applies_to === 'both') && (
            <div className="border border-dashed border-purple-300 rounded-lg p-4 bg-purple-50/50">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-medium text-purple-800">{t('event_detail.have_coupon') || 'Have a coupon code?'}</span>
              </div>
              {!buyerCouponApplied ? (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={buyerCouponCode}
                    onChange={(e) => setBuyerCouponCode(e.target.value.toUpperCase())}
                    placeholder={t('event_detail.enter_coupon') || 'Enter coupon code'}
                    className="flex-1 px-3 py-2 border border-purple-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    disabled={buyerCouponLoading}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => validateCoupon(buyerCouponCode, 'attendee', setBuyerCouponApplied, setBuyerCouponLoading)}
                    disabled={buyerCouponLoading || !buyerCouponCode.trim()}
                    className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  >
                    {buyerCouponLoading ? '...' : (t('event_detail.apply') || 'Apply')}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-green-100 rounded-md px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-green-700 font-medium">{buyerCouponApplied.code}</span>
                    <span className="text-green-600 text-sm">(-{buyerCouponApplied.discount_percent}%)</span>
                  </div>
                  <button
                    onClick={() => { setBuyerCouponApplied(null); setBuyerCouponCode(''); }}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    {t('event_detail.remove') || 'Remove'}
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Price Summary */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">{t('event_detail.entry_fee') || 'Entry Fee'}</span>
              <span className={buyerCouponApplied ? 'line-through text-slate-400' : 'font-medium'}>
                {formatPrice(event?.buyer_fee || 0, true)}
              </span>
            </div>
            {buyerCouponApplied && (
              <>
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t('event_detail.discount') || 'Discount'} ({buyerCouponApplied.discount_percent}%)</span>
                  <span>-{formatPrice((event?.buyer_fee || 0) * buyerCouponApplied.discount_percent / 100, true)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>{t('event_detail.total') || 'Total'}</span>
                  <span className="text-green-700">{formatPrice(getDiscountedPrice(event?.buyer_fee || 0, buyerCouponApplied), true)}</span>
                </div>
              </>
            )}
          </div>
          
          {/* Payment or Free Registration */}
          {getDiscountedPrice(event?.buyer_fee || 0, buyerCouponApplied) <= 0 ? (
            <Button 
              className="w-full"
              data-testid="register-free-btn"
              onClick={async () => {
                try {
                  await axios.post(`${API}/registrations`, {
                    event_id: eventId,
                    coupon_code: buyerCouponApplied?.code || null
                  });
                  if (buyerCouponApplied) {
                    recordCouponUsage(buyerCouponApplied.code, 'attendee');
                  }
                  handleBuyerModalClose(false);
                  setRegistered(true);
                  toast.success(t('event_detail.registration_success') || 'Registration successful! You can now browse exhibitors.');
                  fetchEventData();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to register');
                }
              }}
            >
              <CheckCircle className="h-4 w-4 mr-2" /> {t('event_detail.register_free')}
            </Button>
          ) : (
            <PayPalPayment
              amount={getDiscountedPrice(event?.buyer_fee || 0, buyerCouponApplied)}
              paymentType="buyer_registration"
              description={`Attendee registration for ${event?.name}${buyerCouponApplied ? ` (Coupon: ${buyerCouponApplied.code})` : ''}`}
              pendingEventId={eventId}
              onSuccess={() => {
                if (buyerCouponApplied) {
                  recordCouponUsage(buyerCouponApplied.code, 'attendee');
                }
                handleBuyerModalClose(false);
                setRegistered(true);
                toast.success(t('event_detail.registration_success') || 'Registration successful! You can now browse exhibitors.');
                fetchEventData();
              }}
              onError={(err) => {
                toast.error(t('event_detail.payment_failed') || 'Payment failed. Please try again.');
              }}
              onCancel={() => {
                handleBuyerModalClose(false);
                toast.info(t('event_detail.payment_cancelled') || 'Payment cancelled');
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Registration Payment Modal */}
      <Dialog open={showVendorPayment} onOpenChange={handleVendorModalClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('event_detail.register_vendor') || 'Register as Vendor'}</DialogTitle>
            <DialogDescription>
              {t('event_detail.choose_booth_tier') || 'Choose your booth tier to exhibit at'} {event?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Tier Selection */}
            <div className="grid grid-cols-3 gap-3">
              {['basic', 'premium', 'elite'].map((tier) => {
                const originalPrice = event?.booth_fees?.[tier] || 0;
                const discountedPrice = getDiscountedPrice(originalPrice, vendorCouponApplied);
                return (
                  <button
                    key={tier}
                    onClick={() => setSelectedTier(tier)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedTier === tier 
                        ? 'border-primary bg-primary/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="text-center">
                      {tier === 'elite' && <Crown className="h-6 w-6 mx-auto mb-1 text-purple-600" />}
                      {tier === 'premium' && <Star className="h-6 w-6 mx-auto mb-1 text-orange-500" />}
                      {tier === 'basic' && <ShieldCheck className="h-6 w-6 mx-auto mb-1 text-slate-500" />}
                      <div className="font-semibold capitalize">{t(`event_detail.${tier}`)}</div>
                      {vendorCouponApplied ? (
                        <div>
                          <div className="text-sm line-through text-slate-400">
                            {formatPrice(originalPrice, true)}
                          </div>
                          <div className="text-lg font-bold text-green-600">
                            {formatPrice(discountedPrice, true)}
                          </div>
                        </div>
                      ) : (
                        <div className="text-lg font-bold text-primary">
                          {formatPrice(originalPrice, true)}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Coupon Code Section - Show for exhibitors if coupon enabled and applies to exhibitors or both */}
            {event?.coupon_enabled && (event?.coupon_applies_to === 'exhibitors' || event?.coupon_applies_to === 'both') && (
              <div className="border border-dashed border-purple-300 rounded-lg p-4 bg-purple-50/50">
                <div className="flex items-center gap-2 mb-2">
                  <Tag className="h-4 w-4 text-purple-600" />
                  <span className="text-sm font-medium text-purple-800">{t('event_detail.have_coupon') || 'Have a coupon code?'}</span>
                </div>
                {!vendorCouponApplied ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={vendorCouponCode}
                      onChange={(e) => setVendorCouponCode(e.target.value.toUpperCase())}
                      placeholder={t('event_detail.enter_coupon') || 'Enter coupon code'}
                      className="flex-1 px-3 py-2 border border-purple-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                      disabled={vendorCouponLoading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => validateCoupon(vendorCouponCode, 'exhibitor', setVendorCouponApplied, setVendorCouponLoading)}
                      disabled={vendorCouponLoading || !vendorCouponCode.trim()}
                      className="border-purple-300 text-purple-700 hover:bg-purple-100"
                    >
                      {vendorCouponLoading ? '...' : (t('event_detail.apply') || 'Apply')}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-green-100 rounded-md px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className="text-green-700 font-medium">{vendorCouponApplied.code}</span>
                      <span className="text-green-600 text-sm">(-{vendorCouponApplied.discount_percent}%)</span>
                    </div>
                    <button
                      onClick={() => { setVendorCouponApplied(null); setVendorCouponCode(''); }}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      {t('event_detail.remove') || 'Remove'}
                    </button>
                  </div>
                )}
              </div>
            )}
            
            {/* Price Summary */}
            {vendorCouponApplied && (
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">{t(`event_detail.${selectedTier}`)} {t('event_detail.booth_label')}</span>
                  <span className="line-through text-slate-400">
                    {formatPrice(event?.booth_fees?.[selectedTier] || 0, true)}
                  </span>
                </div>
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t('event_detail.discount') || 'Discount'} ({vendorCouponApplied.discount_percent}%)</span>
                  <span>-{formatPrice((event?.booth_fees?.[selectedTier] || 0) * vendorCouponApplied.discount_percent / 100, true)}</span>
                </div>
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>{t('event_detail.total') || 'Total'}</span>
                  <span className="text-green-700">{formatPrice(getDiscountedPrice(event?.booth_fees?.[selectedTier] || 0, vendorCouponApplied), true)}</span>
                </div>
              </div>
            )}

            {/* PayPal Payment or Free Booth Creation */}
            {getDiscountedPrice(event?.booth_fees?.[selectedTier] || 0, vendorCouponApplied) <= 0 ? (
              <Button 
                className="w-full"
                data-testid="create-booth-free-btn"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/booths`, {
                      event_id: eventId,
                      tier: selectedTier,
                      company_name: user?.name || 'My Company',
                      description: '',
                      coupon_code: vendorCouponApplied?.code || null
                    });
                    if (vendorCouponApplied) {
                      recordCouponUsage(vendorCouponApplied.code, 'exhibitor');
                    }
                    handleVendorModalClose(false);
                    toast.success(t('event_detail.booth_success') || 'Booth registration successful! Set up your booth in the dashboard.');
                    navigate('/dashboard');
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to create booth');
                  }
                }}
              >
                <CheckCircle className="h-4 w-4 mr-2" /> {t('event_detail.create_booth_free')}
              </Button>
            ) : (
              <PayPalPayment
                amount={getDiscountedPrice(event?.booth_fees?.[selectedTier] || 0, vendorCouponApplied)}
                paymentType="booth_fee"
                description={`${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} booth at ${event?.name}${vendorCouponApplied ? ` (Coupon: ${vendorCouponApplied.code})` : ''} (${getCurrencyCode()})`}
                pendingBoothData={{
                  event_id: eventId,
                  tier: selectedTier,
                  company_name: user?.name || 'My Company',
                  description: '',
                }}
                onSuccess={() => {
                  if (vendorCouponApplied) {
                    recordCouponUsage(vendorCouponApplied.code, 'exhibitor');
                  }
                  handleVendorModalClose(false);
                  toast.success(t('event_detail.booth_success') || 'Booth registration successful! Set up your booth in the dashboard.');
                  navigate('/dashboard');
                }}
                onError={(err) => {
                  toast.error(t('event_detail.payment_failed') || 'Payment failed. Please try again.');
                }}
                onCancel={() => {
                  handleVendorModalClose(false);
                  toast.info(t('event_detail.payment_cancelled') || 'Payment cancelled');
                }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Chat disabled - will reconnect when more shows/exhibitors available */}
    </div>
  );
};

export default EventDetailPage;
